import { Component, Output, EventEmitter, signal, inject, effect, OnInit, HostListener, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { ButtonForm } from '../../button-form/button-form';
import { Modal } from '../../modal/modal';
import { AuthService } from '../../../core/services/auth/auth-service';
import { PwaService } from '@/app/core/services/pwa/pwa.service';

import { NgIcon } from '@ng-icons/core';
import { LocalStorageService } from '@/app/core/services/localStorage/local-storage.service';
import { ClubService } from '@/app/core/services/club/club.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { ThemeService } from '@/app/core/services/theme/theme.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, ButtonForm, NgIcon, Modal , RouterLink],
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class Header implements OnInit {
  @Output() toggleSidebar = new EventEmitter<void>();
  private authService = inject(AuthService);
  protected localStorageService = inject(LocalStorageService);
  protected pwaService = inject(PwaService);
  private clubService = inject(ClubService);
  protected profile = this.authService.profile;
  private breakpointObserver = inject(BreakpointObserver);

  isNotMobile$ = this.breakpointObserver
    .observe(['(min-width: 1024px)'])
    .pipe(map(result => result.matches));


  showModal = signal<boolean>(false);
  isDropdownOpen = signal<boolean>(false);
  isSearchFocused = signal<boolean>(false);

  clubs = this.clubService.clubs;
  clubOptions = computed(() => this.clubs().map((c: any) => ({ label: c.name, value: c.id })));
  activeClub = signal<any>(null);
  isClubDropdownOpen = signal<boolean>(false);

  themeService = inject(ThemeService);
  isDark = this.themeService.isDark;

  user = {
    name: 'Admin',
    role: 'Manageur',
    avatar: 'assets/images/user-avatar.png'
  };

  get dashboardLink(): string {
    const role = this.localStorageService.getRole();
    if (role === 'SUPER_ADMIN') return '/super-admin/dashboard';
    if (role === 'ADMIN') return '/admin/dashboard';
    if (role === 'FREELANCE_COACH') return '/coach/dashboard';
    return '/manager/dashboard';
  }

  get profileLink(): string {
    const role = this.localStorageService.getRole();
    if (role === 'SUPER_ADMIN') return '/super-admin/profil';
    if (role === 'ADMIN') return '/admin/profil';
    if (role === 'FREELANCE_COACH') return '/coach/profil';
    return '/manager/profil';
  }

  get settingLink(): string {
    const role = this.localStorageService.getRole();
    if (role === 'SUPER_ADMIN') return '/super-admin/setting';
    if (role === 'ADMIN') return '/admin/setting';
    if (role === 'FREELANCE_COACH') return '/coach/setting';
    return '/manager/setting';
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  constructor() {

    effect(() => {
      const clubsList = this.clubs();
      const role = this.localStorageService.getRole();
      const isClubRole = role === 'ADMIN' || role === 'MANAGER' || role === 'GERANT' || role === 'AGENT';
      if (isClubRole && clubsList.length > 0) {
        const activeId = Number(localStorage.getItem('active_club_id') || this.localStorageService.getClubId());
        const active = clubsList.find((c: any) => c.id === activeId) || clubsList[0];
        if (active) {
          this.activeClub.set(active);
          this.localStorageService.saveSelectedClubId(active.id);
          localStorage.setItem('active_club_id', active.id.toString());
        }
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    this.authService.loadUserProfile().subscribe({
      error: (err) => console.error('Failed to load user profile in header', err)
    });
    this.loadClubs();
  }

  loadClubs() {
    const role = this.localStorageService.getRole();
    const isClubRole = role === 'ADMIN' || role === 'MANAGER' || role === 'GERANT' || role === 'AGENT';
    if (isClubRole) {
      const clubsList = this.clubs();
      if (clubsList.length === 0) {
        this.clubService.getClubs().subscribe({
          next: (res: any) => {
            if (res.success && res.data && res.data.content) {
              const list = res.data.content;
              this.clubService.setClubs(list);
              const activeId = Number(localStorage.getItem('active_club_id') || this.localStorageService.getClubId());
              const active = list.find((c: any) => c.id === activeId) || list[0];
              if (active) {
                this.activeClub.set(active);
                this.localStorageService.saveSelectedClubId(active.id);
                localStorage.setItem('active_club_id', active.id.toString());
              }
            }
          },
          error: (err) => console.error('Failed to load clubs in header', err)
        });
      } else {
        const activeId = Number(localStorage.getItem('active_club_id') || this.localStorageService.getClubId());
        const active = clubsList.find((c: any) => c.id === activeId) || clubsList[0];
        if (active) {
          this.activeClub.set(active);
          this.localStorageService.saveSelectedClubId(active.id);
          localStorage.setItem('active_club_id', active.id.toString());
        }
      }
    }
  }

  @HostListener('document:click')
  closeDropdowns() {
    this.isDropdownOpen.set(false);
    this.isClubDropdownOpen.set(false);
  }

  toggleDropdown() {
    this.isClubDropdownOpen.set(false);
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  toggleClubDropdown() {
    this.isDropdownOpen.set(false);
    this.isClubDropdownOpen.set(!this.isClubDropdownOpen());
    // prevent parent document click from immediately closing the dropdown when opened via button
    // (HostListener on document:click will still fire, so we set a short timeout)
    setTimeout(() => {}, 0);
  }

  selectClub(club: any) {
    this.localStorageService.saveSelectedClubId(club.id);
    this.activeClub.set(club);
    this.isClubDropdownOpen.set(false);
    window.location.reload();
  }

  onClubChange(clubId: any) {
    const club = this.clubs().find((c: any) => c.id === clubId);
    if (club) {
      this.selectClub(club);
    }
  }

  toggleModal() {
    this.isDropdownOpen.set(false);
    this.showModal.set(!this.showModal());
  }

  onLogout() {
    this.authService.logout();
  }

  onSearch(query: string) {
    console.log('Searching for:', query);
  }
}

